import { useState, useEffect, useCallback } from "react";
import Header from "./Header";
import MobileMenu from "./MobileMenu";
import DesktopDrawer from "./DesktopDrawer";
import Footer from "./Footer";
import HeaderSearch from "./HeaderSearch";
import CartDrawer from "./CartDrawer";
import AccountDrawer from "./AccountDrawer";
import WishlistDrawer from "./WishlistDrawer";
import { ArrowUp } from "lucide-react";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { PrivacyPreferencesContext } from "@/contexts/PrivacyPreferencesContext";

const SiteLayout = ({ children }: { children: React.ReactNode }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const cookieConsent = useCookieConsent();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShowTop(window.scrollY > 300);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <PrivacyPreferencesContext.Provider value={{ openPreferences: cookieConsent.openPreferences }}>
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
        <Header
          onMenuOpen={() => setMenuOpen(true)}
          onSearchOpen={() => setSearchOpen(true)}
          onAccountOpen={() => setAccountOpen(true)}
          onWishlistOpen={() => setWishlistOpen(true)}
          onCartOpen={() => setCartOpen(true)}
        />
        <MobileMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
        <DesktopDrawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
        <HeaderSearch isOpen={searchOpen} onOpen={() => setSearchOpen(true)} onClose={() => setSearchOpen(false)} />
        <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
        <AccountDrawer isOpen={accountOpen} onClose={() => setAccountOpen(false)} />
        <WishlistDrawer
          isOpen={wishlistOpen}
          onClose={() => setWishlistOpen(false)}
          onOpenAccount={() => setAccountOpen(true)}
        />
        <main className="pt-[var(--header-height)]">
          {children}
        </main>
        <Footer />

        {/* Fixed back-to-top button */}
        <button
          onClick={scrollToTop}
          aria-label="Back to top"
          className={`fixed bottom-6 right-6 z-50 h-10 w-10 flex items-center justify-center border border-foreground/20 bg-background/80 backdrop-blur-sm text-foreground/60 hover:text-foreground hover:border-foreground/40 transition-all duration-300 ${
            showTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
          }`}
        >
          <ArrowUp size={16} />
        </button>

        <CookieConsentBanner consentState={cookieConsent} />
      </div>
    </PrivacyPreferencesContext.Provider>
  );
};

export default SiteLayout;
